export function normalizeControlChartData(data) {
  return (data || [])
    .map((item) => {
      const rawStepNumber =
        item.stepNumber ??
        item.stepnumber ??
        item.StepNumber ??
        '';

      return {
        ...item,
        stepNumber: String(rawStepNumber).trim(),
        stepName: item.stepName || item.stepname || item.StepName || '',
        values: Array.isArray(item.values)
          ? item.values.map((value) => Number(value))
          : [],
      };
    })
    .filter(
      (item) =>
        item.stepNumber !== '' &&
        item.values.length > 0 &&
        item.values.some((value) => value !== 0)
    );
}

export function sortFilenamesByDate(filenames, sortOrder) {
  return [...filenames].sort((a, b) => {
    const dateA = new Date(a.testStarted);
    const dateB = new Date(b.testStarted);
    return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
  });
}

export function getQuickDateRange(range) {
  const today = new Date();

  if (range === 'today') {
    return { start: today, end: today };
  }

  if (range === 'last7') {
    const start = new Date();
    start.setDate(today.getDate() - 7);
    return { start, end: today };
  }

  if (range === 'thisMonth') {
    return {
      start: new Date(today.getFullYear(), today.getMonth(), 1),
      end: today,
    };
  }

  if (range === 'last30') {
    const start = new Date();
    start.setDate(today.getDate() - 30);
    return { start, end: today };
  }

  return { start: null, end: null };
}

export function findStepData(chartData, selectedStepNumber) {
  return chartData.find(
    (stepData) => String(stepData.stepNumber) === String(selectedStepNumber)
  );
}

const CAPABILITY_D2 = 1.128;

function toFiniteNumber(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function roundMetric(value, digits = 4) {
  return Number.isFinite(value) ? Number(value.toFixed(digits)) : null;
}

export function calculateCapabilityMetrics({ values = [], lsl, usl } = {}) {
  const numericValues = (values || []).map((value) => Number(value)).filter((value) => Number.isFinite(value));
  const sampleCount = numericValues.length;
  const lowerSpecLimit = toFiniteNumber(lsl);
  const upperSpecLimit = toFiniteNumber(usl);

  if (!sampleCount) {
    return {
      sampleCount: 0,
      lsl: lowerSpecLimit,
      usl: upperSpecLimit,
      mean: null,
      min: null,
      max: null,
      range: null,
      mrBar: null,
      sigmaWithin: null,
      sigmaOverall: null,
      specRange: null,
      cp: null,
      cpu: null,
      cpl: null,
      cpk: null,
      pp: null,
      ppu: null,
      ppl: null,
      ppk: null,
      k: null,
      zBench: null,
    };
  }

  const mean = numericValues.reduce((sum, value) => sum + value, 0) / sampleCount;
  const min = Math.min(...numericValues);
  const max = Math.max(...numericValues);
  const range = max - min;
  const movingRanges = numericValues.slice(1).map((value, index) => Math.abs(value - numericValues[index]));
  const mrBar = movingRanges.length
    ? movingRanges.reduce((sum, value) => sum + value, 0) / movingRanges.length
    : 0;
  const sigmaWithin = mrBar > 0 ? mrBar / CAPABILITY_D2 : null;
  const varianceOverall = numericValues.reduce((sum, value) => sum + ((value - mean) ** 2), 0) / sampleCount;
  const sigmaOverall = varianceOverall > 0 ? Math.sqrt(varianceOverall) : 0;

  let specRange = null;
  let cp = null;
  let cpu = null;
  let cpl = null;
  let cpk = null;
  let pp = null;
  let ppu = null;
  let ppl = null;
  let ppk = null;
  let k = null;
  let zBench = null;

  if (lowerSpecLimit !== null && upperSpecLimit !== null && upperSpecLimit > lowerSpecLimit) {
    specRange = upperSpecLimit - lowerSpecLimit;
    const midpoint = (upperSpecLimit + lowerSpecLimit) / 2;
    const halfRange = specRange / 2;
    k = halfRange > 0 ? Math.abs(mean - midpoint) / halfRange : null;

    if (sigmaWithin && sigmaWithin > 0) {
      cp = specRange / (6 * sigmaWithin);
      cpu = (upperSpecLimit - mean) / (3 * sigmaWithin);
      cpl = (mean - lowerSpecLimit) / (3 * sigmaWithin);
      cpk = Math.min(cpu, cpl);
    }

    if (sigmaOverall > 0) {
      pp = specRange / (6 * sigmaOverall);
      ppu = (upperSpecLimit - mean) / (3 * sigmaOverall);
      ppl = (mean - lowerSpecLimit) / (3 * sigmaOverall);
      ppk = Math.min(ppu, ppl);
      zBench = ppk * 3;
    }
  }

  return {
    sampleCount,
    lsl: lowerSpecLimit,
    usl: upperSpecLimit,
    mean: roundMetric(mean),
    min: roundMetric(min),
    max: roundMetric(max),
    range: roundMetric(range),
    mrBar: roundMetric(mrBar),
    sigmaWithin: roundMetric(sigmaWithin),
    sigmaOverall: roundMetric(sigmaOverall),
    specRange: roundMetric(specRange),
    cp: roundMetric(cp),
    cpu: roundMetric(cpu),
    cpl: roundMetric(cpl),
    cpk: roundMetric(cpk),
    pp: roundMetric(pp),
    ppu: roundMetric(ppu),
    ppl: roundMetric(ppl),
    ppk: roundMetric(ppk),
    k: roundMetric(k),
    zBench: roundMetric(zBench),
  };
}

export function buildControlNormalDistributionChartData(selectedData) {
  const values = (selectedData?.values || [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  if (!values.length) {
    return { datasets: [], axisMin: undefined, axisMax: undefined };
  }

  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance = values.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / values.length;
  const stdDev = Math.sqrt(variance);
  const binCount = Math.max(10, Math.ceil(2 * Math.cbrt(values.length)));
  const range = maxVal - minVal;
  const binWidth = range > 0 ? range / binCount : Math.max(Math.abs(mean) * 0.02, 1);
  const histogramBins = Array(binCount).fill(0);

  values.forEach((value) => {
    const binIndex = range > 0
      ? Math.min(binCount - 1, Math.max(0, Math.floor((value - minVal) / binWidth)))
      : Math.floor(binCount / 2);
    histogramBins[binIndex] += 1;
  });

  const axisMin = stdDev > 0 ? Math.min(minVal, mean - 4 * stdDev) : mean - binWidth * 5;
  const axisMax = stdDev > 0 ? Math.max(maxVal, mean + 4 * stdDev) : mean + binWidth * 5;
  const curveStep = (axisMax - axisMin) / 180;
  const fittedCurve = [];

  if (stdDev > 0 && curveStep > 0) {
    for (let x = axisMin; x <= axisMax; x += curveStep) {
      const density = (1 / (stdDev * Math.sqrt(2 * Math.PI))) *
        Math.exp(-0.5 * Math.pow((x - mean) / stdDev, 2));
      fittedCurve.push({ x, y: density * values.length * binWidth });
    }
  } else {
    fittedCurve.push({ x: mean, y: values.length });
  }

  return {
    axisMin,
    axisMax,
    binWidth,
    datasets: [
      {
        type: 'bar',
        label: 'Histogram',
        data: histogramBins.map((count, index) => ({
          x: minVal + (index + 0.5) * binWidth,
          y: count,
          binStart: minVal + index * binWidth,
          binEnd: minVal + (index + 1) * binWidth,
        })),
        backgroundColor: 'rgba(88, 112, 232, 0.72)',
        borderColor: 'rgba(88, 112, 232, 0.9)',
        borderWidth: 1,
        borderRadius: 3,
        barPercentage: 0.92,
        categoryPercentage: 0.92,
        barThickness: 'flex',
        order: 2,
      },
      {
        type: 'line',
        label: 'Normal distribution curve',
        data: fittedCurve,
        borderColor: '#e1008a',
        backgroundColor: '#e1008a',
        borderWidth: 3,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.35,
        fill: false,
        order: 1,
      },
    ],
  };
}

export function buildControlNormalDistributionOptions(chartData) {
  const maxCount = Math.max(
    0,
    ...(chartData?.datasets?.flatMap((dataset) => dataset.data || []).map((point) => Number(point.y) || 0) || [])
  );

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: { position: 'top' },
      title: {
        display: true,
        text: 'Normal Distribution Curve',
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => {
            const raw = tooltipItem.raw || {};
            if (Number.isFinite(raw.binStart) && Number.isFinite(raw.binEnd)) {
              return `${tooltipItem.dataset.label}: ${raw.y} in [${raw.binStart.toFixed(3)}, ${raw.binEnd.toFixed(3)}]`;
            }
            const value = Number(tooltipItem.parsed?.y);
            return `${tooltipItem.dataset.label}: ${Number.isFinite(value) ? value.toFixed(2) : tooltipItem.raw}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'linear',
        min: chartData?.axisMin,
        max: chartData?.axisMax,
        title: {
          display: true,
          text: 'Measure Value',
        },
        grid: { color: 'rgba(148, 163, 184, 0.22)' },
        ticks: {
          callback: (value) => Number(value).toFixed(2),
        },
      },
      y: {
        beginAtZero: true,
        suggestedMax: maxCount ? Math.ceil(maxCount * 1.15) : undefined,
        title: {
          display: true,
          text: 'Occurrences',
        },
        grid: { color: 'rgba(148, 163, 184, 0.22)' },
        ticks: {
          precision: 0,
        },
      },
    },
  };
}
